import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { setToolbar } from '../store/toolbar'
import { setBreadcrumbs } from '../store/breadcrumbs'

import { AgGridReact } from 'ag-grid-react'

import 'ag-grid-community/dist/styles/ag-grid.css'
import 'ag-grid-community/dist/styles/ag-theme-alpine.css'

import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'

import HomeIcon from '@mui/icons-material/HomeRounded'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import LabIcon from '@mui/icons-material/Science'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadIcon from '@mui/icons-material/CloudUpload'
import ClearIcon from '@mui/icons-material/Clear'

import TextField from '@mui/material/TextField'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'

import DialogTitle from '../components/DialogTitle'
import { clinvar } from '../api/clinvar'

import { Icon as IconifyIcon } from "@iconify/react"

import API from '../api/fetchAPI'

import './UserManagement.scss'


const ActionsCellRenderer = (props) => {
    const lab = {
        id: props.data._id,
        shortName: props.data.shortName,
        name: props.data.name,
        website: props.data.website,
        email: props.data.email,
        organizationId: props.data.clinvar?.organizationId,
        contactEmail: props.data.clinvar?.contactEmail,
        hasLogo: !!props.data.logoFileId
    }
    return (
        <>
            <Tooltip title="Edit Lab" placement="top">
                <IconButton className="action-button" aria-label="edit" size="small" onClick={() => props.context.openEditLabDialog(lab)} >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Lab" placement="top">
                <IconButton className="action-button" aria-label="delete" size="small" onClick={() => props.context.openDeleteLabDialog(lab)} >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    )
}


const WebsiteRenderer = (props) => {
    if (!props.value) return null
    return (
        <a href={props.value} target="_blank" rel="noopener noreferrer">
            {props.value}
        </a>
    )
}


const EmailRenderer = (props) => {
    if (!props.value) return null
    return (
        <a href={`mailto:${props.value}`}>
            {props.value}
        </a>
    )
}


function LabForm({ lab, labs, onSave, onCancel }) {
    const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    const [shortName, setShortName] = React.useState(lab?.shortName || '')
    const [name, setName] = React.useState(lab?.name || '')
    const [website, setWebsite] = React.useState(lab?.website || '')
    const [email, setEmail] = React.useState(lab?.email || '')
    const [organizationId, setOrganizationId] = React.useState(lab?.organizationId || '')
    const [contactEmail, setContactEmail] = React.useState(lab?.contactEmail || '')
    const [clinvarInfo, setClinvarInfo] = React.useState(null)
    const [lookupBusy, setLookupBusy] = React.useState(false)
    async function lookupClinvar() {
        setLookupBusy(true); setErrorMessage(null); setClinvarInfo(null)
        try {
            const id = organizationId.trim().match(/(?:^|submitters\/)([1-9]\d*)\/?$/)?.[1]
            if (!id) throw new Error('Ungültige ClinVar Organization ID oder URL.')
            setClinvarInfo(await clinvar(`/submitter/${id}`))
        } catch (e) { setErrorMessage(e.message) } finally { setLookupBusy(false) }
    }

    const [logoFile, setLogoFile] = React.useState(null)
    const [logoPreview, setLogoPreview] = React.useState(lab?.hasLogo ? `/api/labs/logo/${lab.id}` : null)
    const [removeLogo, setRemoveLogo] = React.useState(false)

    const [errorMessage, setErrorMessage] = React.useState(null)
    const [successMessage, setSuccessMessage] = React.useState(null)

    const fileInputRef = React.useRef()

    const handleShortNameChange = (event) => setShortName(event.target.value)
    const handleNameChange = (event) => setName(event.target.value)
    const handleWebsiteChange = (event) => setWebsite(event.target.value)
    const handleEmailChange = (event) => setEmail(event.target.value)
    const handleOrganizationIdChange = (event) => setOrganizationId(event.target.value)

    const handleLogoChange = (event) => {
        const file = event.target.files[0]
        if (!file) return

        const allowed = ['image/png', 'image/jpeg', 'image/svg+xml']
        if (!allowed.includes(file.type)) {
            setErrorMessage('Only PNG, JPEG and SVG files are allowed.')
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage('Logo file must be smaller than 5MB.')
            return
        }

        setLogoFile(file)
        setRemoveLogo(false)
        setErrorMessage(null)

        const reader = new FileReader()
        reader.onload = (e) => setLogoPreview(e.target.result)
        reader.readAsDataURL(file)
    }

    const handleRemoveLogo = () => {
        setLogoFile(null)
        setLogoPreview(null)
        setRemoveLogo(true)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const saveLab = () => {
        if (typeof shortName !== 'string' || shortName.trim().length === 0) {
            setErrorMessage('Short name is required')
            return
        }

        if (typeof name !== 'string' || name.trim().length === 0) {
            setErrorMessage('Name is required')
            return
        }

        if (email && !EMAIL_REGEX.test(email)) {
            setErrorMessage('Wrong email syntax')
            return
        }

        // Check for duplicate names (excluding current lab if editing)
        const otherLabs = labs.filter(l => l._id !== lab?.id)

        if (otherLabs.some(l => l.shortName === shortName.trim())) {
            setErrorMessage('Short name already exists!')
            return
        }

        if (otherLabs.some(l => l.name === name.trim())) {
            setErrorMessage('Name already exists!')
            return
        }

        setErrorMessage(null)

        const labData = {
            shortName: shortName.trim(),
            name: name.trim(),
            website: website.trim(),
            email: email.trim(),
            organizationId: organizationId.trim(),
            contactEmail: contactEmail.trim(),
            logoFile: logoFile,
            removeLogo: removeLogo
        }

        if (lab?.id) {
            labData.id = lab.id
        }

        onSave(labData)
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '400px' }}>
            <TextField
                id="shortName"
                label="Short Name"
                variant="filled"
                size="small"
                required
                value={shortName}
                onChange={handleShortNameChange}
            />
            <TextField
                id="name"
                label="Name"
                variant="filled"
                size="small"
                required
                value={name}
                onChange={handleNameChange}
            />
            <TextField
                id="website"
                label="Website"
                variant="filled"
                size="small"
                value={website}
                onChange={handleWebsiteChange}
            />
            <TextField
                id="email"
                label="Email"
                variant="filled"
                size="small"
                value={email}
                onChange={handleEmailChange}
            />
            <TextField
                id="organizationId"
                label="ClinVar Organization ID oder Submitter-URL"
                variant="filled"
                size="small"
                value={organizationId}
                onChange={handleOrganizationIdChange}
            />
            <TextField label="ClinVar-Kontaktadresse" value={contactEmail} onChange={e => setContactEmail(e.target.value)} size="small" />
            <Button disabled={lookupBusy || !organizationId} onClick={lookupClinvar}>Labordaten aus ClinVar laden</Button>
            {clinvarInfo && <div>
                <p>{clinvarInfo.name}<br />Organization ID: {clinvarInfo.organizationId}</p>
                <TextField select fullWidth label="Kontakt übernehmen" value={clinvarInfo.selectedEmail || clinvarInfo.emails[0] || ''} onChange={e => setClinvarInfo({ ...clinvarInfo, selectedEmail: e.target.value })} SelectProps={{ native: true }}>
                    {!clinvarInfo.emails.length && <option value="">Keine Adresse verfügbar</option>}
                    {clinvarInfo.emails.map(address => <option key={address} value={address}>{address}</option>)}
                </TextField>
                <Button onClick={() => { setOrganizationId(clinvarInfo.organizationId); setContactEmail(clinvarInfo.selectedEmail || clinvarInfo.emails[0] || ''); if (!name) setName(clinvarInfo.name); setClinvarInfo(null) }}>ID und Kontakt ins Formular übernehmen</Button>
            </div>}
            <div style={{ border: '1px dashed #ccc', borderRadius: '4px', padding: '12px' }}>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>Lab Logo</div>
                {logoPreview && (
                    <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                        <img
                            src={logoPreview}
                            alt="Logo preview"
                            style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
                        />
                    </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => fileInputRef.current.click()}
                    >
                        {logoPreview ? 'Change' : 'Upload'} Logo
                    </Button>
                    {logoPreview && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<ClearIcon />}
                            onClick={handleRemoveLogo}
                        >
                            Remove
                        </Button>
                    )}
                </div>
                <div style={{ fontSize: '0.75em', color: '#999', marginTop: '4px' }}>
                    PNG, JPEG or SVG, max 5MB
                </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                <Button onClick={saveLab} variant="contained">
                    {lab?.id ? 'Update' : 'Add'} Lab
                </Button>
                <Button onClick={onCancel} variant="outlined">
                    Cancel
                </Button>
            </div>
            {errorMessage ? <div className="message error-message">{errorMessage}</div> : null}
            {successMessage ? <div className="message success-message">{successMessage}</div> : null}
        </div>
    )
}


export default function LabManagement() {

    const dispatch = useDispatch()
    const gridRef = React.useRef()
    const currentUser = useSelector((state) => state.user)

    const [labs, setLabs] = React.useState(null)

    const breadcrumbs = [
        {
            key: 'home',
            label: 'Home',
            path: '/home',
            icon: HomeIcon
        },
        {
            key: 'admin',
            label: 'Admin',
            path: '/admin',
            icon: AdminIcon
        },
        {
            key: 'labmanagement',
            label: 'Lab Management',
            path: '/admin/labmanagement',
            icon: LabIcon
        }
    ]

    const valueGetter = params => {
        let colId = params.colDef.colId
        return params.data[colId]
    }

    const organizationIdGetter = params => {
        return params.data.clinvar?.organizationId || null
    }

    const columnDefs = [
        {
            colId: '_id',
            field: 'Lab ID',
            filter: false,
            resizable: true,
            valueGetter: valueGetter
        },
        {
            colId: 'shortName',
            field: 'Short Name',
            filter: false,
            resizable: true,
            valueGetter: valueGetter
        },
        {
            colId: 'name',
            field: 'Name',
            filter: false,
            resizable: true,
            valueGetter: valueGetter
        },
        {
            colId: 'website',
            field: 'Website',
            filter: false,
            resizable: true,
            valueGetter: valueGetter,
            cellRenderer: WebsiteRenderer
        },
        {
            colId: 'email',
            field: 'Email',
            filter: false,
            resizable: true,
            valueGetter: valueGetter,
            cellRenderer: EmailRenderer
        },
        {
            colId: 'clinvar.organizationId',
            field: 'ClinVar Org ID',
            filter: false,
            resizable: true,
            valueGetter: organizationIdGetter
        },
        {
            colId: 'actions',
            field: 'Actions',
            filter: false,
            resizable: false,
            sortable: false,
            cellRenderer: ActionsCellRenderer
        }
    ]

    const defaultColDef = React.useMemo(() => ({
        sortable: true
    }))

    React.useEffect(() => {
        dispatch(setBreadcrumbs(breadcrumbs))
        dispatch(setToolbar(renderToolbar()))
        loadLabData()
    }, [])

    const loadLabData = () => {
        API.get('/api/labs/list/').then(response => {
            setLabs(response.data)
        })
        setTimeout(() => { gridRef.current?.api?.sizeColumnsToFit() }, 200)
    }

    const gridSizeChanged = () => {
        gridRef.current?.api?.sizeColumnsToFit()
    }

    const renderToolbar = () =>
        <>
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAddLabDialog}
            >
                Add Lab
            </Button>
        </>

    // Add Lab Dialog
    const [addLabDialogOpen, setAddLabDialogOpen] = React.useState(false)

    const openAddLabDialog = () => {
        setAddLabDialogOpen(true)
    }

    const closeAddLabDialog = () => {
        setAddLabDialogOpen(false)
    }

    const uploadLogo = (labId, logoFile) => {
        const formData = new FormData()
        formData.append('logo', logoFile)
        formData.append('labId', labId)
        return fetch('/api/labs/upload-logo', { method: 'POST', body: formData })
    }

    const deleteLogo = (labId) => {
        return API.post('/api/labs/delete-logo', { params: { labId } })
    }

    const onAddLabSave = (labData) => {
        const { logoFile, removeLogo, ...params } = labData
        API.post('/api/labs/add-lab-admin', {
            params
        }).then(async (response) => {
            if (logoFile && response.id) {
                await uploadLogo(response.id, logoFile)
            }
            closeAddLabDialog()
            loadLabData()
        }).catch((error) => {
            console.error('Error adding lab:', error)
        })
    }

    const renderAddLabDialog = () =>
        <Dialog
            scroll='body'
            maxWidth={false}
            open={addLabDialogOpen}
            onClose={closeAddLabDialog}
        >
            <DialogTitle onClose={closeAddLabDialog}>
                <AddIcon style={{ marginRight: '8px' }} />
                <span>Add Lab</span>
            </DialogTitle>
            <DialogContent>
                <LabForm
                    labs={labs || []}
                    onSave={onAddLabSave}
                    onCancel={closeAddLabDialog}
                />
            </DialogContent>
        </Dialog>

    // Edit Lab Dialog
    const [editLabDialogOpen, setEditLabDialogOpen] = React.useState(false)
    const [editLab, setEditLab] = React.useState(null)

    const openEditLabDialog = (lab) => {
        setEditLab(lab)
        setEditLabDialogOpen(true)
    }

    const closeEditLabDialog = () => {
        setEditLabDialogOpen(false)
        setEditLab(null)
    }

    const onEditLabSave = (labData) => {
        const { logoFile, removeLogo, ...params } = labData
        API.post('/api/labs/update-lab-admin', {
            params
        }).then(async () => {
            if (logoFile && params.id) {
                await uploadLogo(params.id, logoFile)
            } else if (removeLogo && params.id) {
                await deleteLogo(params.id)
            }
            closeEditLabDialog()
            loadLabData()
        }).catch((error) => {
            console.error('Error updating lab:', error)
        })
    }

    const renderEditLabDialog = () =>
        <Dialog
            scroll='body'
            maxWidth={false}
            open={editLabDialogOpen}
            onClose={closeEditLabDialog}
        >
            <DialogTitle onClose={closeEditLabDialog}>
                <EditIcon style={{ marginRight: '8px' }} />
                <span>Edit Lab</span>
            </DialogTitle>
            <DialogContent>
                <LabForm
                    lab={editLab}
                    labs={labs || []}
                    onSave={onEditLabSave}
                    onCancel={closeEditLabDialog}
                />
            </DialogContent>
        </Dialog>

    // Delete Lab Dialog
    const [deleteLabDialogOpen, setDeleteLabDialogOpen] = React.useState(false)
    const [deleteLab, setDeleteLab] = React.useState(null)
    const [deleteError, setDeleteError] = React.useState(null)

    const openDeleteLabDialog = (lab) => {
        setDeleteLab(lab)
        setDeleteError(null)
        setDeleteLabDialogOpen(true)
    }

    const closeDeleteLabDialog = () => {
        setDeleteLabDialogOpen(false)
        setDeleteLab(null)
        setDeleteError(null)
    }

    const executeLabDelete = () => {
        API.post('/api/labs/delete-lab-admin', {
            params: { id: deleteLab.id }
        }).then(() => {
            closeDeleteLabDialog()
            loadLabData()
        }).catch((error) => {
            console.error('Error deleting lab:', error)
            setDeleteError(error.response?.data?.message || 'Error deleting lab')
        })
    }

    const renderDeleteLabDialog = () =>
        <Dialog
            scroll='body'
            maxWidth={false}
            open={deleteLabDialogOpen}
            onClose={closeDeleteLabDialog}
        >
            <DialogTitle onClose={closeDeleteLabDialog}>
                <DeleteIcon style={{ marginRight: '8px' }} />
                <span>Delete Lab</span>
            </DialogTitle>
            <DialogContent>
                <div>
                    Do you want to delete the lab <b>{deleteLab?.name}</b>?<br />
                    <br />
                    <span style={{ color: '#666' }}>
                        Note: You can only delete labs that have no associated users or cases.
                    </span>
                </div>
                {deleteError && (
                    <div className="message error-message" style={{ marginTop: '16px' }}>
                        {deleteError}
                    </div>
                )}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px' }}>
                    <Button
                        onClick={executeLabDelete}
                        variant="contained"
                        color="error"
                    >
                        Delete Lab
                    </Button>
                    <Button
                        onClick={closeDeleteLabDialog}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>

    const renderView = () =>
        <div className="grid-container ag-theme-alpine ag-theme-alpine-modified">
            <AgGridReact
                className="users-grid"
                ref={gridRef}

                columnDefs={columnDefs}
                defaultColDef={defaultColDef}

                rowData={labs}

                animateRows={true}

                onGridSizeChanged={gridSizeChanged}

                enableCellTextSelection={true}

                context={{
                    openEditLabDialog: openEditLabDialog,
                    openDeleteLabDialog: openDeleteLabDialog
                }}
            />
        </div>

    return (
        <>
            {
                renderView()
            }
            {renderAddLabDialog()}
            {renderEditLabDialog()}
            {renderDeleteLabDialog()}
        </>
    )
}